import { t } from '@lingui/macro';
import omit from 'lodash-es/omit';
import partial from 'lodash-es/partial';
import pick from 'lodash-es/pick';

import type { NotificationsResponse } from '@rover/react-lib/src/apis/notificationsCenter.api';
import {
  dismiss,
  get,
  getNextPage,
  read as readAsync,
} from '@rover/react-lib/src/apis/notificationsCenter.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { NotificationsAnnouncements } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type { EventTrackingFields } from '@rover/types';
import APIError from '@rover/utilities/APIError';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

export type EventTrackingNotification = NotificationsAnnouncements & EventTrackingFields;
export type State = {
  count: number;
  unreadCount: number;
  notifications: EventTrackingNotification[];
  next: string | null | undefined;
  previous: string | null | undefined;
  isLoading: boolean;
  errorMessage: string;
  hasOpened: boolean;
};

type Actions = {
  fetchNotificationsPending: ActionCreator<void>;
  fetchNotificationsSuccess: ActionCreator<NotificationsResponse>;
  initializationSuccess: ActionCreator<NotificationsResponse>;
  fetchNotificationsFailure: ActionCreator<string>;
  setNotificationRead: ActionCreator<{
    opk: string;
    read: boolean;
  }>;
  notificationReadEventFired: ActionCreator<string>;
  setErrorMessage: ActionCreator<string>;
  setNotificationDismissed: ActionCreator<{
    opk: string;
    isDismissed: boolean;
  }>;
  removeNotification: ActionCreator<string>;
  notificationClickEventFired: ActionCreator<string>;
  setNotificationsCenterHasOpened: ActionCreator<void>;
};

type Effects = {
  fireNotificationRead: EffectCreator<NotificationsAnnouncements, Promise<void>>;
  fireNotificationDismiss: EffectCreator<NotificationsAnnouncements, Promise<void>>;
  fireNotificationClick: EffectCreator<NotificationsAnnouncements, Promise<void>>;
  fireNotificationCenterOpened: EffectCreator<void, Promise<void>>;
  initialize: EffectCreator<void, Promise<void>>;
  fetchNextPage: EffectCreator<void, Promise<void>>;
  handleNotificationRead: EffectCreator<string, Promise<void>>;
  handleNotificationDismiss: EffectCreator<string, Promise<void>>;
  handleNotificationClick: EffectCreator<string, Promise<void>>;
};

type Selectors = {
  getNotifications: SelectorCreator<NotificationsAnnouncements[]>;
  getNotification: SelectorCreator<EventTrackingNotification | null | undefined>;
  getNotificationsCountText: SelectorCreator<string>;
  isLoading: SelectorCreator<boolean>;
  getNextPageUrl: SelectorCreator<string | null | undefined>;
};

export type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const omitFields: (arg0: EventTrackingNotification) => Notification = partial(
  omit,
  partial.placeholder,
  ['readEventFired', 'clickEventFired']
);

const cleanResponse: (arg0: NotificationsResponse) => {
  count: number;
  unreadCount: number;
  next: string | null | undefined;
  previous: string | null | undefined;
} = partial(pick, partial.placeholder, ['count', 'unreadCount', 'next', 'previous']);
type NotificationEvent =
  | 'notification-viewed'
  | 'notification-clicked'
  | 'notification-dismissed'
  | 'notification-center-open';

const toEvent = (
  event: NotificationEvent,
  notification: NotificationsAnnouncements | null | undefined
) => ({
  event,
  ...(notification
    ? {
        notificationOpk: notification.opk,
      }
    : {}),
  ...(notification && event === 'notification-clicked'
    ? {
        notificationSlug: notification.slug,
      }
    : {}),
});

const mapResults = ({ results }: NotificationsResponse) =>
  results.map((n) => ({ ...n, readEventFired: false, clickEventFired: false }));

export const options: Options = {
  name: 'notificationsCenter',
  initialState: {
    count: 0,
    unreadCount: 0,
    notifications: [],
    next: null,
    previous: null,
    isLoading: false,
    errorMessage: '',
    hasOpened: false,
  },
  actions: {
    fetchNotificationsFailure: (errorMessage) => (state) => ({
      ...state,
      isLoading: false,
      errorMessage,
    }),
    fetchNotificationsPending: () => (state) => ({ ...state, isLoading: true }),

    /**
     * Used for loading the next page during pagination so we only append the new
     * notifications instead of wiping them out with the new results
     */
    fetchNotificationsSuccess: (notificationsResponse) => (state) => ({
      ...state,
      isLoading: false,
      errorMessage: '',
      ...cleanResponse(notificationsResponse),
      notifications: [...state.notifications, ...mapResults(notificationsResponse)],
    }),

    /**
     * Used for initialization (page-load) and when the notifications center
     * is toggled to open (to grab the first page again/make sure the data
     * isn't stale). This action really exists to make sure that when opening
     * the notifications center we completely replace the list of notifications,
     * otherwise we would end up with duplicates.
     */
    initializationSuccess: (notificationsResponse) => (state) => ({
      ...state,
      isLoading: false,
      errorMessage: '',
      ...cleanResponse(notificationsResponse),
      // totally replace the notifications as this is used on page-load
      // _and_ when the notifications center opens
      notifications: mapResults(notificationsResponse),
    }),
    setNotificationRead:
      ({ opk, read }) =>
      (state) => ({
        ...state,
        notifications: state.notifications.map((n) => (n.opk !== opk ? n : { ...n, read })),
      }),
    setErrorMessage: (errorMessage) => (state) => ({ ...state, errorMessage }),
    notificationReadEventFired: (opk: string) => (state) => ({
      ...state,
      notifications: state.notifications.map((n) =>
        n.opk !== opk ? n : { ...n, readEventFired: true }
      ),
    }),
    setNotificationDismissed:
      ({ opk, isDismissed }) =>
      (state) => ({
        ...state,
        notifications: state.notifications.map((n) => (n.opk !== opk ? n : { ...n, isDismissed })),
      }),

    /**
     * Used to handle the following edge case:
     * https://github.com/roverdotcom/web/pull/22432/files#r214436496
     *
     * 1. User Opens dropdown and fetches notifications
     * 2. A notification expires on the backend
     * 3. User reads (or dismisses) that notification
     * 4. Read (or dismiss) endpoint 404s
     */
    removeNotification: (opk: string) => (state) => ({
      ...state,
      notifications: state.notifications.filter((n) => n.opk !== opk),
    }),
    notificationClickEventFired: (opk: string) => (state) => ({
      ...state,
      notifications: state.notifications.map((n) =>
        n.opk !== opk ? n : { ...n, clickEventFired: true }
      ),
    }),
    setNotificationsCenterHasOpened: () => (state) => ({ ...state, hasOpened: true }),
  },
  effects: {
    fireNotificationRead:
      (notification) =>
      async (dispatch, getState, { notificationReadEventFired }): Promise<void> => {
        await Promise.all([
          fireDataLayerEvent(toEvent('notification-viewed', notification)),
          dispatch(notificationReadEventFired(notification.opk)),
        ]);
      },
    fireNotificationDismiss: (notification) => async (): Promise<void> =>
      fireDataLayerEvent(toEvent('notification-dismissed', notification)),
    fireNotificationClick:
      (notification) =>
      async (dispatch, getState, { notificationClickEventFired }): Promise<void> => {
        await dispatch(notificationClickEventFired(notification.opk));
        await fireDataLayerEvent(toEvent('notification-clicked', notification));
      },
    fireNotificationCenterOpened: () => async (): Promise<void> =>
      fireDataLayerEvent(toEvent('notification-center-open', undefined)),
    initialize:
      () =>
      async (
        dispatch,
        getState,
        { fetchNotificationsPending, fetchNotificationsFailure, initializationSuccess },
        _,
        { isLoading }
      ) => {
        if (isLoading(getState())) return;
        dispatch(fetchNotificationsPending());

        try {
          const { data: result } = await get();
          dispatch(initializationSuccess(result));
        } catch (e) {
          if ((e as APIError).status === 401) {
            // Do nothing, it's okay to return no notifications when the user is unauthenticated
          } else {
            dispatch(fetchNotificationsFailure((e as Error).message));
            captureError(e as Error);
          }
        }
      },
    fetchNextPage:
      () =>
      async (
        dispatch,
        getState,
        { fetchNotificationsPending, fetchNotificationsSuccess, fetchNotificationsFailure },
        effects,
        { getNextPageUrl }
      ): Promise<void> => {
        const nextPageUrl = getNextPageUrl(getState());
        if (!nextPageUrl) return;
        dispatch(fetchNotificationsPending());

        try {
          const { data: nextPage } = await getNextPage(nextPageUrl);
          dispatch(fetchNotificationsSuccess(nextPage));
        } catch (e) {
          dispatch(fetchNotificationsFailure((e as Error).message));
          captureError(e as Error);
        }
      },
    handleNotificationRead:
      (opk: string) =>
      async (
        dispatch,
        getState,
        { setNotificationRead, setErrorMessage, removeNotification },
        { fireNotificationRead },
        { getNotification }
      ): Promise<void> => {
        // @ts-expect-error
        const notification = getNotification(getState(), { opk });
        if (!notification) return;
        if (!notification.readEventFired) dispatch(fireNotificationRead(notification));

        if (!notification.read) {
          dispatch(
            setNotificationRead({
              opk: notification.opk,
              read: true,
            })
          );

          try {
            await readAsync(notification.url);
          } catch (e) {
            if ((e as APIError).status === 404) {
              dispatch(removeNotification(opk));
            } else {
              dispatch(setErrorMessage((e as APIError).message));
              dispatch(
                setNotificationRead({
                  opk: notification.opk,
                  read: false,
                })
              );
            }
          }
        }
      },
    handleNotificationDismiss:
      (opk: string) =>
      async (
        dispatch,
        getState,
        { setNotificationDismissed, setErrorMessage, removeNotification },
        { fireNotificationDismiss },
        { getNotification }
      ): Promise<void> => {
        // @ts-expect-error
        const notification = getNotification(getState(), { opk });
        if (!notification) return;
        dispatch(fireNotificationDismiss(notification));
        dispatch(
          setNotificationDismissed({
            opk: notification.opk,
            isDismissed: true,
          })
        );

        try {
          await dismiss(notification.url);
        } catch (e) {
          if ((e as APIError).status === 404) {
            dispatch(removeNotification(opk));
          } else {
            dispatch(setErrorMessage((e as APIError).message));
            dispatch(
              setNotificationDismissed({
                opk: notification.opk,
                isDismissed: false,
              })
            );
          }
        }
      },

    /**
     * If there is no webAction for the notification then we fire
     * the click event on this page. Otherwise we need to redirect
     * to the webAction location so we add the event as query params
     * and do the redirect.
     */
    handleNotificationClick:
      (opk: string) =>
      async (
        dispatch,
        getState,
        actions,
        { fireNotificationClick },
        { getNotification }
      ): Promise<void> => {
        // @ts-expect-error
        const notification = getNotification(getState(), { opk });
        if (!notification || notification.clickEventFired) return;
        await dispatch(fireNotificationClick(notification));
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        if (notification.webAction) window.location.href = notification.webAction;
      },
  },
  selectors: {
    /**
     * Filters dismissed notifications from the notifications in state and
     * maps readEventFired to read so that NotificationsCenterList knows
     * whether to fire the callback when the note scrolls into view
     */
    getNotifications: (getState, createSelector) =>
      createSelector([getState], (s) =>
        s.notifications.reduce(
          (acc, n) =>
            n.isDismissed ? acc : [...acc, omitFields({ ...n, read: n.readEventFired })],
          []
        )
      ),
    getNotification: (getState, createSelector) =>
      createSelector([getState, (state, props) => props.opk], (s, opk: string) =>
        s.notifications.find((n: NotificationsAnnouncements) => n.opk === opk)
      ),
    getNotificationsCountText: (getState, createSelector) =>
      createSelector([getState], ({ unreadCount, hasOpened }) => {
        if (!unreadCount || hasOpened) return '';
        if (unreadCount >= 1 && unreadCount <= 10) return unreadCount.toString();
        return t`10+`;
      }),
    isLoading: (getState, createSelector) => createSelector([getState], (s) => s.isLoading),
    getNextPageUrl: (getState, createSelector) => createSelector([getState], (s) => s.next),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
